import { computed, defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapActions, mapState } from "pinia";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";

import { useCallStore } from "../../../store/useCallStore";
import { useUserStore } from "../../../store/useUsersStore";
import { useConversationStore } from "../../../store/useConversationStore";
import { useDraftsStore } from "../../../store/useDraftsStore";
import eventBus from "../../../../../utilities/eventBus";
import { formatConversationTitle } from "./../../../../../common/vue-helpers/modifiers/format-message";
import type { ISharedAssetDialogOptions } from "../../../../../resources-vue/vue-interfaces/messaging-service/i-message";
import { IPromptOptions } from "../../../../../resources-vue/vue-interfaces/i-prompt";
import { groupNameTooltip } from "../../../../../resources-vue/vue-tooltips/ChatTooltips";
import { MessageService } from "../../../../../services/message-service";
import { ToastrService } from "../../../../../services/toastr.service";
import { ConversationTypeEnum } from "../../../../../enums/ConversationTypeEnum";
import { DraftConvEnum } from "../../../../../enums/DraftConvEnums";

import ConversationSharedFiles from "../ConversationSharedFiles/ConversationSharedFiles.vue";
import TablePrompt from "../../../../../resources-vue/vue-custom-elements/TablePrompt/TablePrompt.vue";
import AudioVideoConferencing from "../AudioVideoConferencing/AudioVideoConferencing.vue";
import Prompt from "../../../../../resources-vue/vue-dialogs/prompt/prompt.vue";
import AvaInitials from "../../../components/AvaInitials/AvaInitials.vue";

export default defineComponent({
    components: {
        TablePrompt,
        AudioVideoConferencing,
        AvaInitials
    },

    validations() {
        return {
            newGroupName: { required }
        };
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    data() {
        return {
            formatConversationTitle,
            dialogRef: null,
            groupNameRef: null,
            hiddenList: "" as string,
            visibleList: "" as string,
            hiddenCount: 0 as number,
            characterLimit: 100 as number,
            userOs: "" as string,
            roomNameTemplate: "" as string,
            newGroupName: "" as string,
            showEditGroupPopup: false as boolean,
            _messageService: null as MessageService,
            _toastrService: null as ToastrService,
            ConversationTypeEnum,
            groupNameTooltip,
            deleteSingleConversationText:
                "This will permanently delete the chat for you - You need to delete anything that you've shared (files, tasks, etc.) separately in the shared file section.",
            deleteGroupConversationText:
                "This will permanently delete the chat for you - but not for others in the group. This also removes you from the chat. You need to delete anything that you've shared (files, tasks, etc.) separately in the shared file section.",

            v$: useVuelidate(),
            isSavingGroupName: false as boolean
        };
    },

    watch: {
        activeConversation: {
            handler() {
                this.getValues();
            },
            deep: true
        }
    },

    created() {
        this._messageService = Container.instance.get(MessageService);
        this._toastrService = Container.instance.get(ToastrService);
    },

    computed: {
        ...mapState(useCallStore, ["currentCallDetails", "showCallHeader"]),
        ...mapState(useUserStore, ["getUser", "getMultipleUsers"]),
        ...mapState(useConversationStore, { activeConversation: "getActiveConversation" }),
        isCallOngoing() {
            return this.showCallHeader.show && this.showCallHeader.convId === this.activeConversation?.id;
        },
        isNameUpdated(): boolean {
            return this.activeConversation?.title?.trim() !== this.newGroupName?.trim();
        }
    },

    methods: {
        ...mapActions(useDraftsStore, ["updateDraftConversation"]),
        ...mapActions(useConversationStore, ["updateConvDetails", "deleteStoreConversation"]),

        getValues() {
            if (this.activeConversation?.loginIds?.length > 0) {
                let userList = this.getMultipleUsers(this.activeConversation.loginIds);
                if (userList?.length > 1) {
                    let visibleFirstNames: string[] = [];
                    let visibleListLength: number = 0;
                    // Get visible names
                    userList.forEach((user) => {
                        // Add 2 for space and commas
                        visibleListLength += user.firstName.length + 2;
                        if (visibleListLength > this.characterLimit) {
                            return;
                        }
                        visibleFirstNames.push(user.firstName);
                    });
                    this.visibleList = visibleFirstNames.join(", ");
                    // Get hidden names count
                    this.hiddenCount = userList.length - visibleFirstNames.length;
                    // Get hidden names
                    this.hiddenList = userList
                        .slice(visibleFirstNames.length)
                        .map((user) => user.firstName)
                        .join(", ");
                } else {
                    this.visibleList = userList[0].firstName + (userList[0].lastName ? ` ${userList[0].lastName}` : "");
                    this.hiddenCount = 0;
                    this.hiddenList = "";
                }
                if (this.activeConversation.title) {
                    this.roomNameTemplate = this.activeConversation.title.split(" ").join("-");
                } else {
                    this.roomNameTemplate = this.visibleList.split(", ").join(" - ");
                }
            }
        },

        async viewSharedFiles() {
            this.userOs = window.sessionStorage.getItem("userOs");
            let sharedFilesDialogOptions: ISharedAssetDialogOptions = {
                title: "Shared Files",
                conversationId: this.activeConversation.id
            };
            this.dialogRef = this.$dialog.open(ConversationSharedFiles, {
                props: {
                    modal: true,
                    closeOnEscape: false,
                    contentClass: this.userOs == "Windows" ? "custom-scrollbar" : "",
                    showHeader: false
                },
                data: {
                    sharedFilesDialogOptions
                }
            });
        },

        handleEditGroupNameClick() {
            this.showEditGroupPopup = !this.showEditGroupPopup;
            this.newGroupName = this.activeConversation?.title ?? "";
        },

        async updateGroupName() {
            const validated: boolean = await this.v$.$validate();
            if (!validated) {
                return;
            }
            if (!this.isNameUpdated) {
                this.cancel();
                return;
            }
            try {
                this.isSavingGroupName = true;
                if (this.activeConversation?.isDraftConversation) {
                    this.updateDraftConversation({
                        id: this.activeConversation.id,
                        newTitle: this.newGroupName,
                        actionType: DraftConvEnum.EditTitle
                    });
                } else await this._messageService.editGroupName(this.activeConversation?.id, this.newGroupName.trim());
                this.cancel();
            } catch (error) {
                console.error(error);
            } finally {
                this.isSavingGroupName = false;
            }
        },

        cancel() {
            this.showEditGroupPopup = false;
            this.newGroupName = "";
        },

        deleteConversation() {
            const deleteMessageText: string =
                this.activeConversation.conversationType == ConversationTypeEnum.SingleConversation
                    ? this.deleteSingleConversationText
                    : this.deleteGroupConversationText;
            const promptOptions: IPromptOptions = {
                title: "Do you want to delete this chat?",
                message: this.activeConversation?.isDraftConversation
                    ? "This will delete the draft group chat"
                    : deleteMessageText,
                okText: "Delete",
                cancelText: "Cancel",
                isForDelete: true,
                isLongText: true
            };

            this.dialogRef = this.$dialog.open(Prompt, {
                props: {
                    modal: true,
                    contentStyle: {
                        overflowX: "hidden"
                    },
                    showHeader: false,
                    style: {
                        width: "620px"
                    }
                },

                data: {
                    promptOptions
                },

                onClose: async (options: any) => {
                    if (options.data === "success") {
                        if (this.activeConversation?.isDraftConversation) {
                            this.updateDraftConversation({
                                id: this.activeConversation.id,
                                actionType: DraftConvEnum.DeleteConv
                            });
                        } else {
                            await this._messageService.deleteConversation(this.activeConversation.id);
                            this.deleteStoreConversation(this.activeConversation.id);
                            eventBus.emit("updateCallLogsAfterDelete", this.activeConversation.id);
                        }
                    }
                }
            });
        },

        async toggleMute() {
            try {
                await this._messageService.toggleMute(!this.activeConversation.isMuted, this.activeConversation.id);

                this._toastrService.success({
                    title: `Conversation ${this.activeConversation.isMuted ? "Unmuted" : "Muted"}`,
                    message: `Successfully ${this.activeConversation.isMuted ? "unmuted" : "muted"} the conversation`
                });
                this.updateConvDetails(this.activeConversation.id, {
                    ...this.activeConversation,
                    isMuted: !this.activeConversation.isMuted
                });
            } catch (error) {
                console.error(error);
                this._toastrService.error({
                    title: `Error`,
                    message: `There was a problem ${
                        this.activeConversation.isMuted ? "unmuting" : "muting"
                    } the conversation. Please try again.`
                });
            }
        },

        routeBacktoConnect() {
            this.$router.push(`/central-messaging/clinicians/conversation`);
        }
    }
});
